/**
 * /platform/testers — тестування силами своїх людей (ч.1126).
 *
 * Дві перевірки для всіх — підтвердження пошти при реєстрації і підтвердження
 * організації для перенесення з 1С — тестувальникам заважають, а вимкнути їх
 * для всіх означає повернути ботів. Тому власник платформи:
 *   • створює посилання для тестувальників: реєстрація за ним — одразу, без
 *     листа, акаунт стає тестовим;
 *   • позначає наявний акаунт тестовим (або знімає позначку).
 * Тестовий акаунт переносить бази з 1С без підтвердження організації і
 * користується Oblik+/AI без обмежень, за рахунок платформи (ч.1173).
 */
import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import { platformTesters, type TesterLink, type TesterTenant } from '@/api/platformTesters';
import { extractApiError } from '@/utils/extractApiError';

const fmtDT = (v: string | null) => (v ? new Date(v).toLocaleString('uk-UA') : '—');

/** Стан посилання словами — те, що побачить людина, яка за ним прийде. */
export function linkState(l: TesterLink): string {
  if (l.revoked_at) return 'відкликано';
  if (l.expires_at && new Date(l.expires_at).getTime() <= Date.now()) return 'строк сплив';
  if (l.max_uses !== null && l.used_count >= l.max_uses) return 'вичерпано';
  return 'діє';
}

export default function PlatformTestersPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['platform-testers'], queryFn: platformTesters.overview });
  const [note, setNote] = useState('');
  const [maxUses, setMaxUses] = useState('10');
  const [validDays, setValidDays] = useState('30');
  const [created, setCreated] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ['platform-testers'] });

  const create = useMutation({
    mutationFn: () => platformTesters.createLink({
      note: note.trim(),
      max_uses: maxUses.trim() ? Number(maxUses) : null,
      valid_days: validDays.trim() ? Number(validDays) : null,
    }),
    onSuccess: (d) => { setCreated(d.url); setCopied(false); setNote(''); setError(null); refresh(); },
    onError: (e) => setError(extractApiError(e, 'Не вдалося створити посилання')),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => platformTesters.revokeLink(id),
    onSuccess: () => { setError(null); refresh(); },
    onError: (e) => setError(extractApiError(e, 'Не вдалося відкликати посилання')),
  });
  const setTester = useMutation({
    mutationFn: (v: { id: string; on: boolean }) => platformTesters.setTenantTester(v.id, v.on),
    onSuccess: () => { setError(null); refresh(); },
    onError: (e) => setError(extractApiError(e, 'Не вдалося змінити позначку акаунта')),
  });

  function onCreate(e: FormEvent) {
    e.preventDefault();
    setError(null); setCreated(null);
    create.mutate();
  }

  async function copy() {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  const linkColumns: ColumnDef<TesterLink>[] = [
    { key: 'note', header: 'Для кого', cell: (l) => l.note || '—', sortValue: (l) => l.note },
    { key: 'created', header: 'Створено', width: 170, cell: (l) => fmtDT(l.created_at), sortValue: (l) => l.created_at },
    {
      key: 'used', header: 'Реєстрацій', width: 120, align: 'right', sortValue: (l) => l.used_count,
      cell: (l) => (l.max_uses === null ? `${l.used_count}` : `${l.used_count} з ${l.max_uses}`),
    },
    { key: 'expires', header: 'Діє до', width: 170, cell: (l) => (l.expires_at ? fmtDT(l.expires_at) : 'без строку') },
    { key: 'state', header: 'Стан', width: 120, cell: (l) => linkState(l), sortValue: (l) => linkState(l) },
    {
      key: 'actions', header: '', width: 120, align: 'right',
      cell: (l) => (
        <button type="button" disabled={!l.usable || revoke.isPending}
          title={l.usable ? undefined : 'Посилання вже не діє'}
          onClick={() => revoke.mutate(l.id)}>
          відкликати
        </button>
      ),
    },
  ];

  const tenantColumns: ColumnDef<TesterTenant>[] = [
    { key: 'name', header: 'Акаунт', cell: (t) => t.name, sortValue: (t) => t.name },
    { key: 'owners', header: 'Власник', cell: (t) => t.owners ?? '—', sortValue: (t) => t.owners ?? '' },
    { key: 'orgs', header: 'Організацій', width: 120, align: 'right', cell: (t) => t.organizations, sortValue: (t) => t.organizations },
    { key: 'created', header: 'Створено', width: 170, cell: (t) => fmtDT(t.created_at), sortValue: (t) => t.created_at },
    {
      key: 'tester', header: 'Тестовий', width: 230, sortValue: (t) => (t.is_tester ? 1 : 0),
      cell: (t) => (t.is_tester
        ? `так${t.link_note ? ` — за посиланням «${t.link_note}»` : ''}`
        : 'ні'),
    },
    {
      key: 'actions', header: '', width: 170, align: 'right',
      cell: (t) => (
        <button type="button" disabled={setTester.isPending}
          onClick={() => setTester.mutate({ id: t.id, on: !t.is_tester })}>
          {t.is_tester ? 'зняти позначку' : 'зробити тестовим'}
        </button>
      ),
    },
  ];

  return (
    <>
      <div className="card">
        <h2 style={{ marginTop: 0 }}>Тестувальники</h2>
        <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginTop: 0 }}>
          Для всіх діють дві перевірки: підтвердження пошти при реєстрації і
          підтвердження організації для перенесення з 1С. Своїм тестувальникам їх
          можна зняти, не відкриваючи двері ботам: дайте посилання для реєстрації
          або позначте наявний акаунт тестовим. Тестовий акаунт переносить бази з 1С
          без підтвердження організації і користується Oblik+/AI без обмежень — за
          рахунок платформи, кредити не списуються; надсилати документи від імені
          непідтвердженої організації він так само не може.
        </p>

        <h3>Посилання для тестувальників</h3>
        <form onSubmit={onCreate}
          style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="field" style={{ margin: 0, minWidth: '16rem' }}>
            <label htmlFor="tl-note">Для кого (примітка)</label>
            <input id="tl-note" value={note} maxLength={200}
              placeholder="наприклад: бухгалтерія, жовтень"
              onChange={(e) => setNote(e.target.value)} />
          </div>
          <div className="field" style={{ margin: 0, width: '9rem' }}>
            <label htmlFor="tl-uses">Реєстрацій, не більше</label>
            <input id="tl-uses" type="number" min={1} value={maxUses}
              onChange={(e) => setMaxUses(e.target.value)} />
          </div>
          <div className="field" style={{ margin: 0, width: '9rem' }}>
            <label htmlFor="tl-days">Діє днів</label>
            <input id="tl-days" type="number" min={1} max={365} value={validDays}
              onChange={(e) => setValidDays(e.target.value)} />
          </div>
          <button type="submit" className="primary" disabled={create.isPending}>
            {create.isPending ? 'створюємо…' : 'створити посилання'}
          </button>
        </form>
        <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
          Порожнє поле — без обмеження. Хто має посилання, той реєструється без
          підтвердження пошти — давайте його лише своїм. Відкликання посилання не
          зачіпає акаунти, вже зареєстровані за ним.
        </p>

        {created && (
          <div className="card" style={{ background: 'var(--bg-soft)', marginBottom: '0.75rem' }}>
            <p style={{ margin: '0 0 0.4rem', fontSize: '0.9rem' }}>
              Посилання створено. Скопіюйте його зараз — пізніше його не буде видно:
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <input readOnly value={created} aria-label="Посилання для тестувальників"
                onFocus={(e) => e.currentTarget.select()} style={{ flex: 1, minWidth: '18rem' }} />
              <button type="button" onClick={copy}>{copied ? 'скопійовано' : 'скопіювати'}</button>
            </div>
          </div>
        )}
        {error && <div className="error">{error}</div>}

        <DataTable
          rows={q.data?.links ?? []}
          columns={linkColumns}
          rowKey={(l) => l.id}
          loading={q.isLoading}
          emptyText="Посилань ще немає."
          rowMuted={(l) => (l.usable ? undefined : 0.55)}
          storageKey="platform-tester-links"
        />
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Акаунти</h3>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: 0 }}>
          Тестовий акаунт — звичайний акаунт: свої організації, користувачі, ролі.
          Позначка відкриває перенесення з 1С без підтвердження організації і Oblik+/AI
          без обмежень (витрати — на сторінці «AI-кредити», колонка собівартості); діє
          одразу і знімається так само. Коли її знято, акаунт і всі дані лишаються і
          працюють далі — для нового перенесення організацію треба буде підтвердити, а
          Oblik+/AI знову працює за кредити акаунта.
        </p>
        <DataTable
          rows={q.data?.tenants ?? []}
          columns={tenantColumns}
          rowKey={(t) => t.id}
          loading={q.isLoading}
          emptyText="Акаунтів немає."
          storageKey="platform-tester-tenants"
        />
      </div>
    </>
  );
}
